import { useCallback, useEffect, useRef } from 'react';
import { Turnstile as TurnstileWidget, type TurnstileInstance } from '@marsidev/react-turnstile';

import { APP_SETTINGS } from '@constants/constants';

import './Turnstile.scss';

// Header the API checks (myome/api util/turnstile.py); value is the widget response token.
export const TURNSTILE_RESPONSE_HEADER = 'CF-Turnstile-Response';
const EXECUTOR_WAIT_MS = 10_000;

// Public sitekey from the active environment entry. Empty omits the widget and sends no header.
export const getTurnstileSiteKey = (): string => APP_SETTINGS.turnstileSiteKey || '';

export const turnstileHeaders = (token: string | null): Record<string, string> =>
  token ? { [TURNSTILE_RESPONSE_HEADER]: token } : {};

type TokenExecutor = () => Promise<string | null>;

// One execute-mode widget per page. Actions call nextTurnstileToken() so each POST gets its own token.
let executor: TokenExecutor | null = null;
const executorWaiters: Array<(execute: TokenExecutor) => void> = [];

export function registerTurnstileExecutor(next: TokenExecutor | null) {
  executor = next;
  if (!next) return;
  executorWaiters.splice(0).forEach(resolve => resolve(next));
}

export function nextTurnstileToken(): Promise<string | null> {
  if (!getTurnstileSiteKey()) return Promise.resolve(null);
  if (executor) return executor();
  return new Promise(resolve => {
    const timer = window.setTimeout(() => {
      const index = executorWaiters.indexOf(onReady);
      if (index >= 0) executorWaiters.splice(index, 1);
      resolve(null);
    }, EXECUTOR_WAIT_MS);
    const onReady = (execute: TokenExecutor) => {
      window.clearTimeout(timer);
      resolve(execute());
    };
    executorWaiters.push(onReady);
  });
}

interface TurnstileProps {
  // Execute: a fresh single-use token per nextTurnstileToken() call.
  execution?: 'execute';
}

const EXECUTE_OPTIONS = { execution: 'execute', appearance: 'interaction-only' } as const;

export const Turnstile = ({ execution = 'execute' }: TurnstileProps) => {
  const siteKey = getTurnstileSiteKey();
  const widgetRef = useRef<TurnstileInstance | undefined>(undefined);
  const executedRef = useRef(false);

  // Tokens are single-use, so reset before every execute after the first. Any failure/timeout is null (fail closed).
  const execute = useCallback((): Promise<string | null> => {
    const widget = widgetRef.current;
    if (!widget) return Promise.resolve(null);
    if (executedRef.current) widget.reset();
    executedRef.current = true;
    widget.execute();
    return widget.getResponsePromise(EXECUTOR_WAIT_MS).catch(() => null);
  }, []);

  const onWidgetLoad = useCallback(() => {
    executedRef.current = false;
    if (execution === 'execute') registerTurnstileExecutor(execute);
  }, [execute, execution]);

  useEffect(() => {
    if (execution !== 'execute') return undefined;
    return () => registerTurnstileExecutor(null);
  }, [execution]);

  if (!siteKey) return null;
  return (
    <TurnstileWidget
      ref={widgetRef}
      siteKey={siteKey}
      options={EXECUTE_OPTIONS}
      onWidgetLoad={onWidgetLoad}
      className='cf-turnstile'
      data-testid='turnstile'
    />
  );
};

// One widget, stuck under the fixed header so a challenge cannot sit below the fold.
export function TurnstileSticky({ className }: { className?: string }) {
  if (!getTurnstileSiteKey()) return null;
  const classes = ['turnstile-sticky', 'sticky-top', 'w-100', className].filter(Boolean).join(' ');
  return (
    <div className={classes} data-testid='turnstile-sticky'>
      <Turnstile execution='execute' />
    </div>
  );
}
